import React from 'react';
import { ProductDetail } from '../../types/ProductTypes';
import {
    approvedStatusToIconMapper,
    marpolRegulationToIconMapper,
    cleaningDifficultyToIconMapper,
    imdgCodeClassToIconMapper,
    marpolPollutionEnumToStringMapper,
    ibcShipTypeEnumToStringMapper,
} from '../../utils/iconMappers';
import { SymbolBarContainer, SymbolButton, SymbolImage, SymbolText } from './SymbolBar.styles';

interface SymbolBarProps {
    product: ProductDetail;
    canEdit?: boolean;
}

interface SymbolItem {
    title: string;
    link?: string;
    popover?: string;
    popoverInnerText?: string;
    icon?: string;
    text?: string;
}

const SymbolBar: React.FC<SymbolBarProps> = ({ product, canEdit = false }) => {
    const generateSymbolBar = (): SymbolItem[] => {
        const symbols: SymbolItem[] = [];

        if (!product) return symbols;

        // Approved Status
        if (canEdit) {
            symbols.push({
                title: 'Approved Status',
                link: 'status',
                popover: `status.approved${product.isApproved}`,
                icon: approvedStatusToIconMapper(product.isApproved),
            });
        }

        // Regulation
        if (product.category?.regulations?.marpolRegulation && product.category.regulations.marpolRegulation !== 0) {
            symbols.push({
                title: 'Regulation',
                link: 'rulesAndRegulations',
                popover: `generalInformation.regulation${product.category.regulations.marpolRegulation}`,
                icon: marpolRegulationToIconMapper(product.category.regulations.marpolRegulation),
            });
        }

        // Pollution Category
        if (product.category?.regulations?.marpolPollutionCategory && product.category.regulations.marpolPollutionCategory !== 0) {
            symbols.push({
                title: 'Pollution Category',
                link: 'rulesAndRegulations',
                popover: `rulesAndRegulations.pollutionCategory${product.category.regulations.marpolPollutionCategory}`,
                text: marpolPollutionEnumToStringMapper(product.category.regulations.marpolPollutionCategory),
            });
        }

        // Ship Type (for IBC regulation)
        if (product.category?.regulations?.marpolRegulation === 2 && product.category?.regulations?.ibcRegulation?.shipType) {
            symbols.push({
                title: 'Ship Type',
                link: 'rulesAndRegulations',
                popover: `rulesAndRegulations.shipType${product.category.regulations.ibcRegulation.shipType}`,
                text: ibcShipTypeEnumToStringMapper(product.category.regulations.ibcRegulation.shipType),
            });
        }

        // USCG Group - check if we have USCG data in regulations
        if (product.regulations?.uscgGroup) {
            symbols.push({
                title: 'USCG Group',
                link: 'uscgCompatibility',
                popoverInnerText: `${product.regulations.uscgGroup.number} - ${product.regulations.uscgGroup.name}`,
                text: `<div class='USCGTitle'>USCG</div><div class='USCGGroup'>${product.regulations.uscgGroup.number}</div>`,
            });
        }

        // Check for flash point warnings based on physical data
        if (product.physicalData?.flashPointRange) {
            const flashPoint = product.physicalData.flashPointRange.minValue?.value || product.physicalData.flashPointRange.maxValue?.value;
            if (flashPoint && flashPoint < 60) {
                const popoverContent =
                    product.category?.regulations?.marpolRegulation === 2 ? 'warnings.lowFlashPointWarningIBC' : 'warnings.lowFlashPointWarningSOLAS';

                symbols.push({
                    title: 'Low Flash Point Warning',
                    link: 'physicalProperties',
                    popover: popoverContent,
                    icon: '/assets/images/LowFlashPointWarning.png',
                });
            }
        }

        // Static Accumulator Warning
        if (product.category?.isStaticAccumulator === 1) {
            symbols.push({
                title: 'Static Accumulator Warning',
                link: 'physicalProperties',
                popover: 'warnings.staticAccumulatorWarning',
                icon: '/assets/images/StaticAccumulatorWarning.png',
            });
        }

        // Cleaning Difficulty
        const cleaningProcedure = product.procedures?.find((procedure) => procedure.category === 1);
        if (cleaningProcedure && cleaningProcedure.score && cleaningProcedure.score > 0) {
            symbols.push({
                title: 'Cleaning Difficulty',
                link: 'cleaningProcedure',
                popover: `cleaningProcedures.cleaningDifficulty${cleaningProcedure.score}`,
                icon: cleaningDifficultyToIconMapper(cleaningProcedure.score),
            });
        }

        // IMDG Class
        if (product.regulations?.imdgCodeClass) {
            symbols.push({
                title: 'IMDG Class',
                link: 'emergencyScenarios',
                popover: `generalInformation.hazards${product.regulations.imdgCodeClass.classification?.[0]}`,
                icon: imdgCodeClassToIconMapper(product.regulations.imdgCodeClass.classification),
            });
        }

        return symbols;
    };

    const symbols = generateSymbolBar();

    if (symbols.length === 0) {
        return null;
    }

    const handleSymbolClick = (link?: string) => {
        if (link) {
            // Scroll to section or handle navigation
            const element = document.getElementById(link);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    return (
        <SymbolBarContainer>
            {symbols.map((symbol, index) => (
                <SymbolButton key={index} title={symbol.title} onClick={() => handleSymbolClick(symbol.link)}>
                    {symbol.icon && <SymbolImage src={symbol.icon} alt={symbol.title} />}
                    {symbol.text && (
                        <SymbolText
                            dangerouslySetInnerHTML={{
                                __html: symbol.text,
                            }}
                        />
                    )}
                </SymbolButton>
            ))}
        </SymbolBarContainer>
    );
};

export default SymbolBar;
